<template>
  <div class="content">
    <h1 class="title">{{ $t("dashboard.title") }}</h1>

    <v-row no-gutters class="mt-5" justify="center">
      <v-col cols="12" sm="8" md="4" lg="3" class="c-dashboardInfo mx-3">
        <div class="wrap">
          <h4
            class="heading heading5 hind-font medium-font-weight c-dashboardInfo__title"
          >
            {{ $t("dashboard.todayGrossIncome") }}
          </h4>
          <span
            v-if="!isLoading"
            class="hind-font counter caption-12 c-dashboardInfo__count"
            >{{ todayGrossIncome }}</span
          >
          <v-progress-circular
            v-else
            size="40"
            indeterminate
            class="loading-circle my-3"
          ></v-progress-circular>
          <span class="hind-font caption-12 c-dashboardInfo__subInfo">{{
            $t("dashboard.currency")
          }}</span>
        </div>
      </v-col>
      <v-col cols="12" sm="8" md="4" lg="3" class="c-dashboardInfo mx-3">
        <div class="wrap">
          <h4
            class="heading heading5 hind-font medium-font-weight c-dashboardInfo__title"
          >
            {{ $t("dashboard.todaySoldProducts") }}
          </h4>
          <span
            v-if="!isLoading"
            class="hind-font counter caption-12 c-dashboardInfo__count"
            >{{ todaySoldProducts }}</span
          >
          <v-progress-circular
            v-else
            size="40"
            indeterminate
            class="loading-circle my-3"
          ></v-progress-circular>
          <span class="hind-font caption-12 c-dashboardInfo__subInfo">{{
            $t("dashboard.each")
          }}</span>
        </div>
      </v-col>
    </v-row>

    <div class="table">
      <div class="inputs">
        <v-row>
          <v-col cols="12" xs="12" lg="3" md="4" sm="6">
            <v-text-field
              :label="$t('dashboard.productNameLabel')"
              v-model="product_name"
              @keydown.enter="handleFilter(false)"
            ></v-text-field>
          </v-col>
          <v-col cols="12" xs="12" lg="3" md="4" sm="6" v-if="isSuperAdmin">
            <v-select
              :label="$t('dashboard.partnerLabel')"
              v-model="partner"
              :items="partnersWithId"
              item-title="name"
              item-value="id"
              clearable
              @keydown.enter="handleFilter(false, true)"
              :readonly="enterClicked ? true : false"
            ></v-select>
          </v-col>
        </v-row>
        <v-btn @click="handleFilter(false)">{{
          $t("dashboard.filterButton")
        }}</v-btn>
      </div>
      <div>
        <v-data-table
          v-if="!isLoading"
          :headers="headers"
          :items="capacityList"
          disable-sort
          hide-default-footer
          :items-per-page="-1"
          class="elevation-0"
        >
          <template v-slot:[`item.due_date`]="{ item }">
            {{ formatDateString(item.selectable.due_date) }}
          </template>
          <template v-slot:[`item.max_capacity`]="{ item }">
            {{ item.selectable.max_capacity + " fő" }}
          </template>
          <template v-slot:[`item.used_capacity`]="{ item }">
            {{ item.selectable.used_capacity + " fő" }}
          </template>
          <template v-slot:[`item.capacity_percent`]="{ item }">
            {{ item.selectable.capacity_percent + "%" }}
          </template>
          <template v-slot:[`item.weather`]="{ item }">
            {{ item.selectable.weather + "°C" }}
          </template>
          <template v-slot:no-data>
            <Vue3Lottie
              :animationData="NoDataLottie"
              :height="200"
              :width="200"
            />
            <div class="pb-5 table-no-data">
              <h3>{{ $t("tableNoDataTitle") }}</h3>
              <p>{{ $t("tableNoDataDesc") }}</p>
            </div>
          </template>
          <template v-slot:bottom>
            <div class="botSide">
              <v-row align="center" justify-sm="end" justify="center" class="px-2">
                <v-col cols="12" xs="12" lg="3" md="4" sm="6">
                  <v-row align="center" justify="end"
                    ><div class="pr-2 pageSelectorLabel">
                      {{ $t("anomaly.tableFooterText") }}
                    </div>
                    <v-select
                      v-model="limit"
                      hide-details
                      @update:model-value="handleFilter(false)"
                      :items="pageSizes"
                      class="pageSelector pl-5"
                    ></v-select>
                    <div class="pl-2">
                      {{ displayRange }}
                    </div>
                  </v-row>
                </v-col>
                <v-col
                  cols="12"
                  xs="12"
                  lg="3"
                  md="4"
                  sm="5"
                  class="pl-1 pr-1 pagination"
                >
                  <v-pagination
                    v-model="page"
                    :length="pageCount"
                    rounded="circle"
                    :total-visible="1"
                    show-first-last-page="true"
                    @update:model-value="handleFilter(true)"
                  ></v-pagination>
                </v-col>
              </v-row>
            </div>
          </template>
        </v-data-table>
      </div>
      <v-card v-if="isLoading" class="loading">
        <v-progress-circular
          v-if="isLoading"
          size="73.5"
          indeterminate
          class="loading-circle"
        ></v-progress-circular>
      </v-card>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { Vue3Lottie } from "vue3-lottie";
import NoDataLottie from "@/assets/lotties/nodatalottie.json";

export default {
  name: "DashboardView",
  props: {},
  components: {
    Vue3Lottie,
  },
  data() {
    return {
      NoDataLottie,
      headers: [
        {
          title: this.$t("dashboard.table.headers.par_name"),
          key: "partner.par_name",
          align: "start",
          sortable: false,
        },
        {
          title: this.$t("dashboard.table.headers.name"),
          key: "name",
          sortable: false,
        },
        {
          title: this.$t("dashboard.table.headers.due_date"),
          key: "due_date",
          sortable: false,
        },
        {
          title: this.$t("dashboard.table.headers.max_capacity"),
          key: "max_capacity",
          sortable: false,
        },
        {
          title: this.$t("dashboard.table.headers.used_capacity"),
          key: "used_capacity",
          sortable: false,
        },
        {
          title: this.$t("dashboard.table.headers.capacity_percent"),
          key: "capacity_percent",
          sortable: false,
        },
        {
          title: this.$t("dashboard.table.headers.weather"),
          key: "weather",
          sortable: false,
        },
      ],

      todayGrossIncome: 0,
      todaySoldProducts: 0,

      //Pagination settings
      page: undefined,
      limit: undefined,
      total: 0,
      pageSizes: [10, 25, 50, 100],

      //For Search
      product_name: undefined,
      partner: undefined,
      partnersWithId: [],

      capacityList: [],

      isLoading: false,
      error: false,

      enterClicked: false,
    };
  },
  created() {
    this.getPartnerList();
    this.handleGetDashBoardStatistics();
  },
  mounted() {},
  methods: {
    ...mapActions({
      getDashBoardStatistics: "statistics/getDashBoardStatistics",
      getPartners: "users/getPartners",
    }),

    handleGetDashBoardStatistics(body) {
      this.isLoading = true;
      this.getDashBoardStatistics(body)
        .then((res) => {
          this.isLoading = false;

          const todayGrossIncomeCounter = () => {
            const target = +res.sum_gross_unit;
            const count = +this.todayGrossIncome;
            const increment = target / 200;
            if (count < target) {
              this.todayGrossIncome = `${Math.ceil(count + increment)}`;
              setTimeout(todayGrossIncomeCounter, 1);
            } else this.todayGrossIncome = target;
          };

          const todaySoldProductsCounter = () => {
            const target = +res.sold_products;
            const count = +this.todaySoldProducts;
            const increment = target / 200;
            if (count < target) {
              this.todaySoldProducts = `${Math.ceil(count + increment)}`;
              setTimeout(todaySoldProductsCounter, 1);
            } else this.todaySoldProducts = target;
          };

          todayGrossIncomeCounter();
          todaySoldProductsCounter();

          this.capacityList = res.capacityList.list;
          this.page = res.capacityList.page;
          this.limit = res.capacityList.limit;
          this.total = res.capacityList.total;
        })
        .catch((err) => {
          console.log(err);
          this.isLoading = false;
          this.error = true;
        });
    },
    getPartnerList() {
      if (this.isSuperAdmin) {
        this.getPartners()
          .then((response) => {
            this.partnersWithId = response.result.map((partner) => {
              return { id: partner.id, name: partner.par_name };
            });
          })
          .catch((err) => {
            console.log(err);
          });
      }
    },
    formatDateString(dateString) {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = ("0" + (date.getMonth() + 1)).slice(-2);
      const day = ("0" + date.getDate()).slice(-2);
      const formattedDate = `${year}.${month}.${day}.`;
      return formattedDate;
    },
    handleFilter(hasPage, keydown) {
      if (keydown) {
        this.enterClicked = true;
      }
      const requestBody = {
        par_id: this.partner !== undefined ? this.partner : null,
        name: this.product_name !== undefined ? this.product_name : null,
        limit: this.limit,
        page: hasPage ? this.page : 1,
      };
      this.handleGetDashBoardStatistics(requestBody);
      setTimeout(() => {
        this.enterClicked = false;
      }, 100);
    },
  },
  computed: {
    ...mapGetters({ loggedInUser: "users/loggedInUser" }),
    pageCount() {
      return Math.ceil(this.total / this.limit);
    },
    displayRange() {
      const start = (this.page - 1) * this.limit + 1;
      const end = Math.min(start + this.limit - 1, this.total);
      return `${start}-${end} of ${this.total}`;
    },
    isSuperAdmin() {
      return this.loggedInUser && this.loggedInUser.role === 0;
    },
  },
};
</script>

<style lang="scss" scoped>
.c-dashboardInfo {
  margin-bottom: 15px;
}
.c-dashboardInfo .wrap {
  background: #ffffff;
  box-shadow: 2px 10px 20px rgba(0, 0, 0, 0.1);
  border-radius: 7px;
  text-align: center;
  position: relative;
  overflow: hidden;
  padding: 40px 25px 20px;
  height: 100%;
  display: flex;
  flex-direction: column;
}
.counter {
  margin-top: auto;
}
.loading-circle {
  margin-left: auto;
  margin-right: auto;
  margin-top: auto !important;
}
.c-dashboardInfo__title,
.c-dashboardInfo__subInfo {
  color: #6c6c6c;
  font-size: 1.18em;
}
.c-dashboardInfo__title {
  margin-bottom: 16px;
}
.c-dashboardInfo span {
  display: block;
}
.c-dashboardInfo__count {
  font-weight: 600;
  font-size: 2.5em;
  // line-height: 64px;
  line-height: 52px;
  color: #323c43;
}
.c-dashboardInfo .wrap:after {
  display: block;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 10px;
  content: "";
}

.c-dashboardInfo:nth-child(1) .wrap:after {
  background: linear-gradient(82.59deg, #00c48c 0%, #00a173 100%);
}
.c-dashboardInfo:nth-child(2) .wrap:after {
  background: linear-gradient(81.67deg, #0084f4 0%, #1a4da2 100%);
}
.c-dashboardInfo:nth-child(3) .wrap:after {
  background: linear-gradient(69.83deg, #0084f4 0%, #00c48c 100%);
}
.c-dashboardInfo:nth-child(4) .wrap:after {
  background: linear-gradient(81.67deg, #ff647c 0%, #1f5dc5 100%);
}
.c-dashboardInfo__title svg {
  color: #d7d7d7;
  margin-left: 5px;
}
.MuiSvgIcon-root-19 {
  fill: currentColor;
  width: 1em;
  height: 1em;
  display: inline-block;
  font-size: 24px;
  transition: fill 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
  user-select: none;
  flex-shrink: 0;
}
</style>
